import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import * as _ from "lodash";
import { NzTableModule } from "ng-zorro-antd/table";
import { MessageService } from "primeng/api";
import { Observable, Subscription } from "rxjs";
import * as uuid from "uuid";
import { LoadingComponent } from "../../../components/common/loading/loading.component";
import { MenuDropdownComponent } from "../../../components/common/menu-dropdown/menu-dropdown.component";
import { CmdRestWebServiceModalComponent } from "../../../components/modal-templates/cmd-rest-web-service-modal/cmd-rest-web-service-modal.component";
import { DeleteConfirmationComponent } from "../../../components/modal-templates/delete-confirmation/delete-confirmation.component";
import { EditorGenerateConfirmationComponent } from "../../../components/modal-templates/editor-generate-confirmation/editor-generate-confirmation.component";
import { EditorSettingsModalComponent } from "../../../components/modal-templates/editor-settings-modal/editor-settings-modal.component";
import { EditorItemModalComponent } from "../../../components/modal-templates/generic-modal/editor-item-modal.component";
import { SaveScriptModalComponent } from "../../../components/modal-templates/save-script-modal/save-script-modal.component";
import { ScriptMappingComponent } from "../../../components/modal-templates/script-mapping/script-mapping.component";
import { StepsConfirmationModalComponent } from "../../../components/modal-templates/steps-confirmation-modal/steps-confirmation-modal.component";
import { ValidateRecordedActivitiesComponent } from "../../../components/modal-templates/validate-recorded-activities/validate-recorded-activities.component";
import { selectCommandDefinitions } from "../../../core/ngrx/selectors/automation.selectors";
import { MaterialModule } from "../../../core/shared/material.module";
import { codeViewTableColumns } from "../../../core/table-templates/code-view-table";
import { credentialTableColumns } from "../../../core/table-templates/credential-table";
import { MessageEnum } from "../../../core/utils/message.enum";
import { BaseLayoutComponent } from "../../base-layout.component";

/**
 * The `EditorTestScriptComponent` is responsible for managing and editing test scripts.
 * It provides functionalities for loading, selecting, and saving recorded test activities.
 * The component interacts with a side navigation tree, controls subscriptions for events,
 * and manages modal dialogs for editing specific items.
 */
@Component({
  selector: "app-editor-test-script",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MaterialModule,
    LoadingComponent,
    NzTableModule,
    LoadingComponent,
    MatOptionModule,
    MatSelectModule,
    DeleteConfirmationComponent,
    MenuDropdownComponent
  ],
  templateUrl: "./editor-test-script.component.html",
  styleUrl: "./editor-test-script.component.scss",
})
export class EditorTestScriptComponent extends BaseLayoutComponent {
  /**
   *List of scripts to be displayed and managed in the component.
   */
  @Input()
  steps: any = [];

  @Input()
  meta: any = {};

  @Input()
  versions: any = {
    active: "",
    list: [],
  };

  @Input()
  displayVersionList = false;

  @Input()
  isDataLoading = false;

  @Input()
  isAgentic = false;

  @Input()
  intellisenseList: string[] = [];

  @Input()
  selectedAgent: any;

  @Input()
  isAiRecorderEnabled = false;

  @Input()
  isApiAutomationEnabled = false;

  @Output() actions = new EventEmitter();

  commandDefinitions: any = [];
  indx = 1;
  contextMenu = ["Edit", "Delete"];
  tooltipText = 'Enable AI Recorder, API Automation in asset configuraion & Map test case to proceed';
  tooltipTextAiRecorder = 'Enable AI Recorder, API Automation in asset configuraion & Map test case to proceed';

  private messageService = inject(MessageService);

  columns = codeViewTableColumns;
  dropzoneHovered = false;
  dropzoneActive = false;
  agentList: any[] = [];

  isAllChecked = false;
  isIndeterminate = false;
  loaderText = "";

  private modalService = inject(NgbModal);
  private controlSubscription = new Subscription();

  mapOfExpandedData: { [key: string]: any[] } = {};
  stackMap: { [key: string]: any[] } = { past: [], current: [] };
  lastHoveredRow: any;
  expandedRowIds: string[] = [];
  copiedItems: any;
  testCaseName = "Ts_1_V.01";
  selectedRows: any[] = [];
  commandDefinitions$: Observable<any> | undefined;
  expandableTypeIds: string[] = [
    "CMD_DEFAULT_IF",
    "CMD_DEFAULT_ELSE",
    "CMD_DEFAULT_FOR",
    "CMD_DEFAULT_ELSE_IF",
    "CMD_DEFAULT_TRY",
    "CMD_DEFAULT_CATCH",
    "CMD_DEFAULT_CONTINUE",
    "CMD_DEFAULT_WHILE",
    "CMD_DEFAULT_FOREACH",
    "CMD_DEFAULT_FINALLY"
  ];
  /** Continuations that must travel with their opener when reordering. */
  private readonly controlBlockContinuations: Record<string, string[]> = {
    CMD_DEFAULT_IF: ["CMD_DEFAULT_ELSE_IF", "CMD_DEFAULT_ELSE"],
    CMD_DEFAULT_TRY: ["CMD_DEFAULT_CATCH", "CMD_DEFAULT_FINALLY"],
  };
  private readonly controlBlockClosers = new Set([
    "CMD_DEFAULT_ELSE",
    "CMD_DEFAULT_FINALLY",
  ]);
  private readonly controlBlockFollowers = new Set([
    "CMD_DEFAULT_ELSE_IF",
    "CMD_DEFAULT_ELSE",
    "CMD_DEFAULT_CATCH",
    "CMD_DEFAULT_FINALLY",
  ]);
  paramIds: any = {};
  isSaving = false;
  isTableDataReady = false;
  formattedPayload: any;
  selectedScriptId: string = "";
  selectedScriptLabel: any = [];
  isMapped: boolean = true;
  isLLMConfigured: boolean = false;
  restApiDraftVariables: any = {};
  globalEngines: any = [];
  disableRunBtn = false;

  /**
   *  Lifecycle hook for initializing the component.
   * Configures the side navigation tree, loads recorded activities,
   * and sets up control subscriptions for event monitoring.
   */
  ngOnInit() {
    if (this.meta?.script?.tcgTestCaseResultName) {
      this.isMapped = true;
    } else {
      this.isMapped = false;
    }

    this.route.params.subscribe((params: any) => {
      this.paramIds = params;
      this.loadProjectConfig();
      this.loadAgents();
      this.proceedInitialProcess();
    });
    this.getConfiguredLLMs();
  }

  constructMenuDropdownForExecutionEngine() {
    this.globalEngines = [
      {
        label: 'Devices',
        options: []
      },
      {
        label: 'Execution Engines',
        options: []
      }
    ];

    let devices: any = [];
    let agents: any = [];

    // Mobile types
    let mobileTypes: any = [];
    this.meta.devices?.forEach((ele: any) => {
      if (!mobileTypes.includes(ele.environment)) {
        mobileTypes.push(ele.environment);
      }
    });
    mobileTypes.forEach((ele: any) => {
      devices.push({
        label: `${ele} Device`,
        options: this.meta.devices?.filter((item: any) => item.environment === ele)
      });
    });

    // Agent types
    let agentTypes: any = [];
    this.agentList?.forEach((ele: any) => {
      if (!agentTypes.includes(ele.type)) {
        agentTypes.push(ele.type);
      }
    });
    agentTypes.forEach((ele: any) => {
      agents.push({
        label: `${ele} Execution Engines`,
        options: this.agentList?.filter((item: any) => item.type === ele)
      });
    });

    this.globalEngines[0].options = devices;
    this.globalEngines[1].options = agents;

  }

  getConfiguredLLMs() {
    this.restService.getLLMConfs(this.paramIds.projectId).subscribe((response: any) => {
      if (response?.data?.isCommonLLM) {
        console.log("LLM configured globally!")
      } else if (response?.data?.applicationLLMs) {
        const configuredLLms = response?.data?.applicationLLMs;
        const key = 'aiRecorder';

        if (key in configuredLLms) {
          //asset mapped true, checking llm configuration in mapped assets
          if (configuredLLms[key]?.llmId === null || configuredLLms[key]?.llmId === undefined) {
            this.isLLMConfigured = false;
            this.tooltipTextAiRecorder = this.isMapped ? "Please configure the LLM connection  to AI Recorder before proceeding with asset actions." : this.tooltipText;
          } else {
            this.isLLMConfigured = true;
            this.tooltipTextAiRecorder = this.tooltipText;
          }
        }
      } else {
        console.log("Warning: No LLMs configured!")
      }
    }, error => {
      console.log("Error occurred while getting the configurred llms!");
    });
  }

  loadAgents() {
    this.agentList = [];
    this.restService.getAgents(this.paramIds.projectId).subscribe(
      (res: any) => {
        if (res.status === 200) {
          this.agentList.push({
            type: 'Ungrouped',
            name: 'On Demand',
            label: 'On Demand',
            value: 'on_demand'
          });
          res.data?.filter((ele: any) => {
            if (ele.active) {
              ele.label = ele.name;
              if (ele.label.length > 22) {
                ele.label = `${ele.label.slice(0, 22)}...`;
              }
              ele.value = ele.id;
              this.agentList.push(ele);
            }
          });
          this.constructMenuDropdownForExecutionEngine();
        }
      },
      () => {
        this.popToaster("", false, true);
      }
    );
  }

  onAgentSelect(val: any) {
    this.selectedAgent = val?.value;
  }

  runAgent() {
    if (!this.selectedAgent) {
      return;
    }
    const isDevice = this.meta.devices?.some(
      (d: any) => d.id === this.selectedAgent || d.value === this.selectedAgent
    ) ?? false;
    let payload: any = {
      agentId: (this.selectedAgent === 'on_demand' ? null : this.selectedAgent) || null,
      agentType: this.selectedAgent
        ? this.agentList.find((ele: any) => ele.id === this.selectedAgent)?.type
        : "CLUSTER",
      device: isDevice,
    };
    if (this.selectedAgent === 'on_demand') {
      payload.agentType = 'CLUSTER';
    }
    this.isDataLoading = true;
    this.loaderText = "Please wait! Invoking Execution Engine...";
    this.restService
      .runAgent(
        this.paramIds.projectId,
        this.paramIds.automationId,
        this.paramIds.scriptId,
        payload
      )
      .subscribe(
        (res: any) => {
          if (res.status === 200) {
            if (!payload.agentId) {
              // add new on demand agent to list
              let onDemandAgent = res.data;
              if (onDemandAgent.name.length > 22)
                onDemandAgent.name = `${onDemandAgent.name.slice(0, 22)}...`;
              this.agentList.push(onDemandAgent);
              // this.selectedAgent = onDemandAgent.id;
              setTimeout(() => {
                window.open(res.data?.configurations?.url, "_blank");
                this.isDataLoading = false;
              }, 20000);
            } else {
              this.popToaster("Execution Engine Invoked - Success");
              this.isDataLoading = false;
            }
          }
        },
        (error: any) => {
          this.popToaster(
            error?.message || "Something went wrong while running agent!",
            false
          );
          this.isDataLoading = false;
        }
      );
  }

  openMappingModal() {
    const modalRef = this.modalService.open(ScriptMappingComponent, {
      centered: true,
      windowClass: "scrip-mapping-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.data = this.paramIds;
    modalRef.result.then(
      () => {
        // Closed after a successful mapping: reload to show the mapped name.
        this.getScriptById(this.meta.script.id);
        this.proceedInitialProcess();
      },
      () => {
        // Dismissed (X): nothing was mapped, so there is nothing to reload.
      }
    );
  }

  proceedInitialProcess() {
    this.commandDefinitions$ = this.store.select(selectCommandDefinitions);
    this.commandDefinitions$.subscribe((commands) => {
      if (commands?.length) {
        this.commandDefinitions = commands;
        this.indx = 1;
        this.reOrderRowNumbers(this.steps, 0, true);
        this.stackMap["current"] = [];
        this.stackMap["current"].push(_.cloneDeep(this.steps));
        this.reOrderExpandableRows();
        this.isTableDataReady = true;
      }
    });
  }

  loadProjectConfig() {
    this.restService.getAutomationProjectDetails(this.paramIds.projectId, this.paramIds.automationId).subscribe((res: any) => {
      if (res?.data?.frameworkType && res.data.frameworkType === 'Appium JavaScript') {
        this.disableRunBtn = true;
      }
    });
  }

  /**
   * Selects all scripts upon loading and updates the selection in the table.
   */
  selectAllOnLoad() {
    this.onSelectAll({ target: { checked: true } });
    this.handleRowSelect(this.steps);
  }

  /**
   * Handles the selection of rows in the table.
   * @param rows - The selected rows.
   */
  handleRowSelect(rows: any) {
    console.log(rows);
  }

  /**
   * Handles drag-and-drop events. Opens a modal dialog for editing if the dragged item is moved to a different container.
   * @param event - The drag-and-drop event.
   */
  drop(event: any) {
    this.dropzoneHovered = false;
    if (event.container.id !== event.previousContainer.id) {
      this.openGenericModal(event?.item?.data, event.container.id === "list-2");
    }
    this.dropzoneActive = true;
  }

  /**
   *  Opens a generic modal dialog with the specified title.
   * @param title - The title to display in the modal.
   */
  openGenericModal(item: any, addToLast: boolean = false) {
    let fields: any = item.parameters;

    let stepDefinition = this.commandDefinitions.find(
      (definition: any) => definition.id === item.id
    );
    let returnType = stepDefinition?.returnType;
    let isReturnVariableRequired = stepDefinition?.isReturnTypeRequired;

    let variables = this.meta.variables?.filter(
      (ele: any) => ele.dataType === returnType
    );
    let isFoundInd = fields?.findIndex(
      (ele: any) => ele.dataType === "variableSelect"
    );

    if (isFoundInd >= 0) {
      fields[isFoundInd].labelDescription = variables?.length
        ? ""
        : `No ${returnType} Variables Found`;
      fields[isFoundInd].options = variables?.map((ele: any) => ({
        ...ele,
        label: ele.name,
        value: ele.id,
      }));
    }
    if (returnType && isFoundInd < 0) {
      fields.push({
        dataType: "variableSelect",
        options: variables.map((ele: any) => ({
          ...ele,
          label: ele.name,
          value: ele.id,
        })),
        isOptional: !isReturnVariableRequired,
        id: "variableSelect",
        isActive: true,
        placeholder: `Select ${item.returnType} Variable`,
        name: "Return Variable",
        labelDescription: variables?.length
          ? ""
          : `No ${returnType} Variables Found`,
      });
    }

    //if param_page has variable
    const pageFieldIndex = fields.findIndex((f: any) => f.id === "param_page");
    let customPageField = {
      description: "Page of the UIObject required to click",
      dataType: "Page",
      isOptional: false,
      order: 1,
      placeholder: "SelectPage",
      customInput: false,
      id: "param_page",
      isActive: true,
      name: "Page",
      type: "param_ui_object",
      uiParams: {}
    };
    if (pageFieldIndex !== -1) {
      fields[pageFieldIndex] = customPageField;
    }

    //if ui object's locator has variable
    let customField = {
      type: "param_ui_object",
      dataType: "Uiobject",
      isOptional: false,
      placeholder: "SelectUIObject",
      customInput: false,
      id: "param_ui_object",
      isActive: true,
      name: "UIObject",
      uiParams: {}
    };
    const index = fields.findIndex((f: any) => f.id === "param_ui_object");
    if (index !== -1) {
      fields[index] = customField;
    }

    if (item.id ==="CMD_DEFAULT_ADD_EXPRESSION" ) {
      fields[0].type = "textarea";
      fields[0].dataType = "textarea";
    }
    if (item.id === 'CMD_DEFAULT_CALL_REST_WEB_SERVICE') {
      const modalRef = this.modalService.open(CmdRestWebServiceModalComponent, {
        centered: true,
        size: 'lg',
        backdrop: 'static',
      });
      modalRef.componentInstance.services = this.meta?.services;
      modalRef.componentInstance.routeParams = this.paramIds;
      modalRef.componentInstance.variables = this.meta.variables;
      modalRef.componentInstance.restApiDraftVariables = this.restApiDraftVariables;
      modalRef.componentInstance.id = item.id;
      modalRef.componentInstance.intellisense = this.intellisenseList;
      modalRef.result.then((data: any) => {
        this.addStepToScript(data, addToLast, item);
      }, () => {
        this.dropzoneActive = false;
      });
      modalRef.componentInstance.fields = fields;
    } else {
      // Generic Modal
      const modalRef = this.modalService.open(EditorItemModalComponent, {
        centered: true,
        windowClass: "add-item-modal",
        backdrop: 'static',
      });
      modalRef.componentInstance.configs = {
        title: item.displayName,
        intellisenseList: item.id === 'CMD_DEFAULT_ADD_EXPRESSION' ? [] : this.intellisenseList,
      };
      let filterredMeta = _.cloneDeep(this.meta);
      filterredMeta.application = filterredMeta.application.filter((ele: any) => ele.paradigm === stepDefinition.paradigm);
      modalRef.componentInstance.fields = fields;
      modalRef.componentInstance.meta = filterredMeta;
      modalRef.componentInstance.routeParams = this.paramIds;
      modalRef.componentInstance.definitionId = item.id;
      modalRef.result.then(
        (data: { fields: any[]; value: any }) => {
          this.addStepToScript(data, addToLast, item);
        }, () => {
          this.dropzoneActive = false;
        });
    }
  }

  addStepToScript(data: any, addToLast: boolean, item: any) {
    this.dropzoneActive = false;
    let ind = this.steps.findIndex(
      (item: any) => item.row_id === this.lastHoveredRow?.row_id
    );
    if (!this.lastHoveredRow) addToLast = true;
    let returnVar = data.fields.find(
      (ele: any) => ele.dataType === "variableSelect"
    );
    let row: any = {
      level: addToLast
        ? 0
        : this.lastHoveredRow.children
          ? this.lastHoveredRow.level + 1
          : this.lastHoveredRow.level,
      value: item.displayName,
      commandDefinitionId: item.id,
      stepParameters: data.fields.filter(
        (ele: any) => ele.dataType !== "variableSelect"
      ),
    };

    if (returnVar?.value) {
      let varObj = this.meta.variables.find(
        (ele: any) => ele.id === returnVar.value
      );
      row.returnVariable = _.cloneDeep(varObj);
    }

    if (this.expandableTypeIds.includes(item.id)) row.children = [];
    this.stackMap["past"].push(_.cloneDeep(this.steps));

    row.row_id = row.row_id || uuid.v4();
    if (addToLast) {
      row.level = 0;
      this.steps.push(row);
      this.indx = 1;
      this.reOrderRowNumbers(this.steps);
      this.steps = [...this.steps];
    } else if (this.lastHoveredRow?.children) {
      this.addItemToParent(this.steps, this.lastHoveredRow.row_id, row);
    } else if (this.lastHoveredRow?.parent) {
      this.addItemToParentByIndex(
        this.steps,
        this.lastHoveredRow.parent.row_id,
        row
      );
    } else {
      this.steps = [
        ...this.steps.slice(0, ind + 1),
        row,
        ...this.steps.slice(ind + 1, this.steps.length),
      ];
      this.indx = 1;
      this.reOrderRowNumbers(this.steps);
      this.steps = [...this.steps];
    }
    this.stackMap["current"] = [];
    this.stackMap["current"].push(_.cloneDeep(this.steps));
    this.reOrderExpandableRows();
  }

  addItemToParentByIndex(arr: any[], parentId: string, row: any) {
    let isResolved = false;
    arr.forEach((item: any) => {
      const ind = item.children?.findIndex(
        (ele: any) => ele.row_id === this.lastHoveredRow.row_id
      );
      if (!isResolved && ind !== undefined && ind >= 0) {
        item.children = [
          ...item.children.slice(0, ind + 1),
          row,
          ...item.children.slice(ind + 1, item.children.length),
        ];
        isResolved = true;
        this.indx = 1;
        this.reOrderRowNumbers(this.steps);
        this.steps = [...this.steps];
      } else if (item.children) {
        this.addItemToParentByIndex(item.children, parentId, row);
      }
    });
  }

  addItemToParent(arr: any[], parentId: string, row: any) {
    arr.forEach((item: any) => {
      let isResolved = false;
      if (item.row_id === parentId) {
        row.level = item.level + 1;
        item.children.push(row);
        isResolved = true;
        this.indx = 1;
        this.reOrderRowNumbers(this.steps);
        this.steps = [...this.steps];
      }
      if (item.children?.length && !isResolved) {
        this.addItemToParent(item.children, parentId, row);
      }
    });
  }

  /**
   * Lifecycle hook for cleaning up resources when the component is destroyed.
   * Unsubscribes from control events and resets the side navigation tree.
   */
  ngOnDestroy() {
    this.controlSubscription.unsubscribe();
    this.scriptEditorControlsService.isHeaderEnabled(false);
  }

  collapse(array: any[], data: any, $event: boolean): void {
    data.expand = $event;
    if (data.children && !$event) {
      data.children.forEach((d: any) => {
        const target = array.find((a) => a.row_id === d.row_id)!;
        target.expand = $event;
        this.collapse(array, target, $event);
      });
    }
  }

  isAlreadyExpanded(id: string, childId?: string) {
    let isFound;
    if (childId) {
      isFound = this.mapOfExpandedData[id]?.find(
        (ele: any) => ele.row_id === childId
      );
    } else {
      isFound = this.mapOfExpandedData[id]?.find(
        (ele: any) => ele.row_id === id
      );
    }
    return isFound?.expand ? true : false;
  }

  getPlaceholderFromDefinition(value: string) {
    return (
      this.commandDefinitions.find((definition: any) => definition.id === value)
        ?.placeholder || ""
    );
  }

  /**
   * Resolve a step parameter label for the table. Prefer persisted displayValue;
   * for GlobalVariable look up the name from meta when only the id is present.
   */
  resolveParamDisplayValue(param: any): string {
    if (param?.displayValue) {
      return param.displayValue;
    }

    const valueId = param?.value;
    if (valueId == null || valueId === "") {
      return "";
    }

    switch (param?.dataType?.toLowerCase()) {
      case "globalvariable": {
        const match = this.meta?.globalvariable?.find(
          (ele: any) => String(ele.value ?? ele.id) === String(valueId)
        );
        return match?.label || match?.name || match?.displayName || "";
      }
      default:
        return "";
    }
  }

  /** Rebuild placeholders + expanded rows after meta (e.g. global variables) is ready. */
  refreshStepPlaceholders() {
    if (!this.steps?.length) return;
    this.indx = 1;
    this.reOrderRowNumbers(this.steps, 0, false);
    this.reOrderExpandableRows();
    this.steps = [...this.steps];
  }

  convertTreeToList(root: any): any[] {
    const stack: any[] = [];
    const array: any[] = [];
    const hashMap = {};
    stack.push({
      ...root,
      level: 0,
      expand: this.isAlreadyExpanded(root.row_id),
      row_id: root.row_id || uuid.v4(),
    });

    while (stack.length !== 0) {
      const node = stack.pop()!;
      if (node.children) {
        for (let i = node.children.length - 1; i >= 0; i--) {
          stack.push({
            ...node.children[i],
            level: node.level! + 1,
            expand: this.isAlreadyExpanded(
              root.row_id,
              node.children[i].row_id
            ),
            parent: node,
            row_id: node.children[i].row_id || uuid.v4(),
          });
        }
      }
      this.visitNode(node, hashMap, array);
    }
    return array;
  }

  visitNode(
    node: any,
    hashMap: { [key: string]: boolean },
    array: any[]
  ): void {
    if (!hashMap[node.row_id]) {
      hashMap[node.row_id] = true;
      array.push(node);
    }
  }

  tablePositionChange(event: CdkDragDrop<string[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const draggedItem = event.item.data?.item;
    if (!draggedItem) {
      return;
    }

    const flat = this.buildVisibleFlatIndex();
    const targetFlat = flat[event.currentIndex];
    const targetItem = this.lastHoveredRow || targetFlat?.item;
    if (!targetItem || targetItem.row_id === draggedItem.row_id) {
      return;
    }

    const movingDown = event.currentIndex > event.previousIndex;
    this.stackMap["past"].push(_.cloneDeep(this.steps));

    const nestContainer = this.getNestContainer(targetItem);
    if (nestContainer && this.canMoveIntoContainer(draggedItem.row_id, nestContainer.row_id)) {
      // Dropped on If/Else/For/etc. (or on a child inside one) → nest into that block.
      if (this.isExpandableContainer(targetItem)) {
        this.moveBlockIntoContainer(draggedItem.row_id, nestContainer.row_id);
      } else {
        this.moveBlockIntoContainer(
          draggedItem.row_id,
          nestContainer.row_id,
          targetItem.row_id,
          movingDown
        );
      }
    } else {
      // Same-level reorder (keep If/Else and Try/Catch together).
      const sourceList = this.findSiblingList(this.steps, draggedItem.row_id);
      const targetList = this.findSiblingList(this.steps, targetItem.row_id);
      if (sourceList && sourceList === targetList) {
        this.moveBlockInList(
          sourceList,
          draggedItem.row_id,
          targetItem.row_id,
          movingDown
        );
      } else if (sourceList === this.steps && targetFlat) {
        const targetRoot = this.steps[targetFlat.rootIndex];
        if (targetRoot) {
          this.moveBlockInList(
            this.steps,
            draggedItem.row_id,
            targetRoot.row_id,
            movingDown
          );
        }
      }
    }

    this.stackMap["current"] = [];
    this.stackMap["current"].push(_.cloneDeep(this.steps));
    this.reInitiateTable();
  }

  /** Visible rows in the same order the CDK drop list renders them. */
  private buildVisibleFlatIndex(): { item: any; rootIndex: number }[] {
    const flat: { item: any; rootIndex: number }[] = [];
    this.steps.forEach((root: any, rootIndex: number) => {
      const expanded = this.mapOfExpandedData[root.row_id] || [root];
      expanded.forEach((item: any) => {
        if ((item.parent && item.parent.expand) || !item.parent) {
          flat.push({ item, rootIndex });
        }
      });
    });
    return flat;
  }

  private isExpandableContainer(item: any): boolean {
    return (
      !!item &&
      (Array.isArray(item.children) ||
        this.expandableTypeIds.includes(item.commandDefinitionId))
    );
  }

  /** Container that should receive a nested drop for the hovered/target row. */
  private getNestContainer(targetItem: any): any | null {
    if (!targetItem) {
      return null;
    }
    if (this.isExpandableContainer(targetItem)) {
      return this.findNodeById(this.steps, targetItem.row_id);
    }
    if (targetItem.parent?.row_id) {
      return this.findNodeById(this.steps, targetItem.parent.row_id);
    }
    return null;
  }

  private canMoveIntoContainer(draggedRowId: string, containerRowId: string): boolean {
    if (!draggedRowId || !containerRowId || draggedRowId === containerRowId) {
      return false;
    }
    const draggedNode = this.findNodeById(this.steps, draggedRowId);
    if (!draggedNode) {
      return false;
    }
    // Do not nest a node into itself or into one of its own descendants.
    if (this.findNodeById(draggedNode.children || [], containerRowId)) {
      return false;
    }
    return true;
  }

  private findNodeById(arr: any[], rowId: string): any | null {
    for (const item of arr || []) {
      if (item.row_id === rowId) {
        return item;
      }
      if (item.children?.length) {
        const found = this.findNodeById(item.children, rowId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /** Remove If/Else (or single step) block from wherever it currently lives. */
  private extractBlockByRowId(arr: any[], rowId: string): any[] | null {
    const index = arr.findIndex((item: any) => item.row_id === rowId);
    if (index >= 0) {
      const { start, end } = this.getControlBlockRange(arr, index);
      return arr.splice(start, end - start + 1);
    }
    for (const item of arr) {
      if (item.children?.length) {
        const extracted = this.extractBlockByRowId(item.children, rowId);
        if (extracted) {
          return extracted;
        }
      }
    }
    return null;
  }

  /**
   * Move a command block into an expandable container (If / Else / For / ...).
   * When `nearChildRowId` is set, insert before/after that child.
   */
  private moveBlockIntoContainer(
    draggedRowId: string,
    containerRowId: string,
    nearChildRowId?: string,
    movingDown = true
  ): void {
    const container = this.findNodeById(this.steps, containerRowId);
    if (!container) {
      return;
    }
    const block = this.extractBlockByRowId(this.steps, draggedRowId);
    if (!block?.length) {
      return;
    }
    if (!Array.isArray(container.children)) {
      container.children = [];
    }
    container.expand = true;

    if (nearChildRowId) {
      let insertAt = container.children.findIndex(
        (item: any) => item.row_id === nearChildRowId
      );
      if (insertAt < 0) {
        container.children.push(...block);
      } else {
        if (movingDown) {
          insertAt += 1;
        }
        container.children.splice(insertAt, 0, ...block);
      }
    } else {
      container.children.push(...block);
    }
  }

  /** Sibling array that contains the row with `rowId` (top-level or nested). */
  private findSiblingList(arr: any[], rowId: string): any[] | null {
    if (arr.some((item: any) => item.row_id === rowId)) {
      return arr;
    }
    for (const item of arr) {
      if (item.children?.length) {
        const found = this.findSiblingList(item.children, rowId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /**
   * Move a command (and its If/Else or Try/Catch trailer) from its current
   * position relative to the target row inside the same sibling list.
   */
  private moveBlockInList(
    list: any[],
    draggedRowId: string,
    targetRowId: string,
    movingDown: boolean
  ): void {
    const fromIndex = list.findIndex((item: any) => item.row_id === draggedRowId);
    if (fromIndex < 0) {
      return;
    }
    const { start, end } = this.getControlBlockRange(list, fromIndex);
    if (targetRowId) {
      const targetInBlock = list
        .slice(start, end + 1)
        .some((item: any) => item.row_id === targetRowId);
      if (targetInBlock) {
        return;
      }
    }

    let toIndex = list.findIndex((item: any) => item.row_id === targetRowId);
    if (toIndex < 0) {
      return;
    }
    // If the drop target is itself a block opener, place relative to that whole block.
    const targetBlock = this.getControlBlockRange(list, this.getBlockStartIndex(list, toIndex));
    toIndex = movingDown ? targetBlock.end + 1 : targetBlock.start;

    const block = list.splice(start, end - start + 1);
    if (toIndex > start) {
      toIndex -= block.length;
    }
    toIndex = Math.max(0, Math.min(toIndex, list.length));
    list.splice(toIndex, 0, ...block);
  }

  /** If the row is Else/Catch/etc., walk back to the matching If/Try opener. */
  private getBlockStartIndex(list: any[], index: number): number {
    const cmd = list[index]?.commandDefinitionId;
    if (!this.controlBlockFollowers.has(cmd)) {
      return index;
    }
    const opener =
      cmd === "CMD_DEFAULT_CATCH" || cmd === "CMD_DEFAULT_FINALLY"
        ? "CMD_DEFAULT_TRY"
        : "CMD_DEFAULT_IF";
    for (let i = index; i >= 0; i--) {
      if (list[i]?.commandDefinitionId === opener) {
        return i;
      }
      if (
        !this.controlBlockFollowers.has(list[i]?.commandDefinitionId) &&
        list[i]?.commandDefinitionId !== opener
      ) {
        break;
      }
    }
    return index;
  }

  /** Contiguous If + Else-If* + Else (or Try + Catch* + Finally) range. */
  private getControlBlockRange(
    list: any[],
    index: number
  ): { start: number; end: number } {
    const start = this.getBlockStartIndex(list, index);
    const opener = list[start]?.commandDefinitionId;
    const continuations = this.controlBlockContinuations[opener];
    if (!continuations) {
      return { start, end: start };
    }
    let end = start;
    for (let i = start + 1; i < list.length; i++) {
      const id = list[i]?.commandDefinitionId;
      if (!continuations.includes(id)) {
        break;
      }
      end = i;
      if (this.controlBlockClosers.has(id)) {
        break;
      }
    }
    return { start, end };
  }

  reInitiateTable() {
    this.indx = 1;
    this.reOrderRowNumbers(this.steps);
    this.reOrderExpandableRows();
    this.steps = [...this.steps];
  }

  onRowHover(item: any, mapId: string) {
    this.lastHoveredRow = item;
    let ind = this.mapOfExpandedData[mapId].findIndex(
      (ele: any) => ele.row_id === item.row_id
    );
    this.mapOfExpandedData[mapId][ind].hovered = true;
    if (this.dropzoneHovered && item.children) {
      this.mapOfExpandedData[mapId][ind].expand = true;
    }
  }

  onMouseLeave(item: any, mapId: string) {
    let ind = this.mapOfExpandedData[mapId].findIndex(
      (ele: any) => ele.row_id === item.row_id
    );
    this.mapOfExpandedData[mapId][ind].hovered = false;
  }

  reOrderExpandableRows() {
    this.steps.forEach((item: any) => {
      if (!item.row_id) {
        item.row_id = uuid.v4();
      }
      this.mapOfExpandedData[item.row_id] = this.convertTreeToList(item);
    });
  }

  reOrderRowNumbers(arr: any[], level: number = 0, isInitial = false) {
    if (!arr) return;
    arr.forEach((script: any) => {
      script.order = this.indx;
      script.level = level;
      script.row_id = script.id || script.row_id || uuid.v4();
      if (this.expandableTypeIds.includes(script.commandDefinitionId)) {
        if (isInitial) {
          script.children = script.children?.length
            ? script.children?.sort((a: any, b: any) => a?.sequence - b?.sequence)
            : script.steps?.sort((a: any, b: any) => a?.sequence - b?.sequence) || [];
        } else {
          script.children = script.children?.length
            ? script.children
            : script.steps || [];
        }
      }
      let placeholder = this.getPlaceholderFromDefinition(
        script.commandDefinitionId
      );
      script.stepParameters?.forEach((param: any) => {
        if (placeholder.includes(`{${param.id}}`)) {
          const display = this.resolveParamDisplayValue(param);
          if (display) {
            param.displayValue = display;
          }
          placeholder = placeholder.replace(
            `{${param.id}}`,
            display || param.value
          );
        }
      });
      script.placeholder = placeholder;
      this.indx++;
      if (script.children) {
        this.reOrderRowNumbers(script.children, level + 1);
      }
    });
  }

  onRowSelectChange(event: any, row: any): void {
    const allSelected = this.steps.every((record: any) => record.rowSelect);
    const anySelected = this.steps.some((record: any) => record.rowSelect);
    this.isAllChecked = allSelected;
    this.isIndeterminate = !allSelected && anySelected;
    if (event) {
      this.selectedRows.push(row);
    } else {
      let ind = this.selectedRows.findIndex(
        (ele: any) => ele.row_id === row.row_id
      );
      this.selectedRows.splice(ind, 1);
    }
    this.appendDataToRow(this.steps, row.row_id, { rowSelect: event });
    if (row.children) {
      this.checkAllNodes(row.children, event, true);
      this.reInitiateTable();
    }
  }

  appendDataToRow(arr: any[], targetId: string, data: any) {
    let isResolved = false;
    arr.forEach((ele: any, index: number) => {
      if (!isResolved && ele.row_id === targetId) {
        arr[index] = { ...ele, ...data };
        isResolved = true;
      }
      if (!isResolved && ele.children) {
        this.appendDataToRow(ele.children, targetId, data);
      }
    });
  }

  replaceDataInRow(arr: any[], targetId: string, key: string, data: any) {
    let isResolved = false;
    arr.forEach((ele: any, index: number) => {
      if (!isResolved && ele.row_id === targetId) {
        arr[index][key] = data;
        isResolved = true;
      }
      if (!isResolved && ele.children) {
        this.replaceDataInRow(ele.children, targetId, key, data);
      }
    });
  }

  onSelectAll(event: any): void {
    this.isAllChecked = event.target.checked;
    this.selectedRows = [];
    this.isIndeterminate = false;
    this.checkAllNodes(this.steps, event.target.checked);
    this.reInitiateTable();
  }

  checkAllNodes(
    arr: any[],
    isChecked: boolean,
    appendRowDisableProp?: boolean
  ) {
    arr.forEach((item: any) => {
      item.rowSelect = isChecked;
      if (isChecked) this.selectedRows.push(item);
      if (appendRowDisableProp) {
        item.disableRowSelect = isChecked;
      }
      if (item.children) {
        this.checkAllNodes(item.children, isChecked, appendRowDisableProp);
      }
    });
  }

  deleteSelectedRows() {
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass:"delete-confirmation-window",
      centered: true,
      size: "sm",
      backdrop: "static",
    });

    // Set confirmation modal data (optional if your modal accepts dynamic content)
    modalRef.componentInstance.data = {
      title: "Confirm Deletion",
      description: `Are you sure you want to delete selected test steps?`,
    };
    modalRef.result
      .then((confirmed) => {
        if (confirmed) {
          // Proceed only if user confirms

          this.checkAllNodes(this.steps, false);
          this.stackMap["past"].push(_.cloneDeep(this.steps));
          let ids = this.selectedRows.map((a) => a.row_id);

          if (this.isAllChecked) {
            this.steps = [];
          } else {
            this.deleteOnRecursive(this.steps, ids);
          }

          this.stackMap["current"] = [];
          this.stackMap["current"].push(_.cloneDeep(this.steps));
          this.selectedRows = [];
          this.reInitiateTable();
          this.popToaster("Test Steps Deleted - Success");
        }
      })
      .catch(() => {
        // User closed the modal without confirming
      });
  }

  deleteOnRecursive(arr: any[], ids: string[]) {
    for (let i = 0; i < arr.length; i++) {
      if (ids.includes(arr[i].row_id)) {
        arr.splice(i, 1);
        i = 0;
      }
      if (arr[i]?.children) {
        this.deleteOnRecursive(arr[i].children, ids);
      }
    }
  }

  setUniqueId(arr: any, removeAccessibilityProps?: boolean) {
    arr.forEach((ele: any) => {
      ele.row_id = uuid.v4();
      delete ele.id;
      if (removeAccessibilityProps) {
        delete ele.rowSelect;
        delete ele.disableRowSelect;
        delete ele.expand;
      }
      if (ele.children) {
        this.setUniqueId(ele.children, removeAccessibilityProps);
      }
    });
  }

  copyCommands() {
    this.copiedItems = _.cloneDeep(this.selectedRows);
  }

  pasteCommands() {
    this.setUniqueId(this.copiedItems, true);
    this.checkAllNodes(this.steps, false);
    this.stackMap["past"].push(_.cloneDeep(this.steps));
    this.steps = [...this.steps, ..._.cloneDeep(this.copiedItems)];
    // this.copiedItems = [];
    this.selectedRows = [];
    this.stackMap["current"] = [];
    this.stackMap["current"].push(this.steps);
    this.reInitiateTable();
  }

  handleIconActions(action: string) {
    switch (action) {
      case "copy":
        this.copyCommands();
        break;
      case "paste":
        this.pasteCommands();
        break;
      case "delete":
        this.deleteSelectedRows();
        break;
      case "undo":
        this.switchStack("undo");
        break;
      case "redo":
        this.switchStack("redo");
        break;
    }
  }

  switchStack(type: "undo" | "redo") {
    if (type === "undo") {
      let data = this.stackMap["past"].pop();
      this.stackMap["current"].push(data);
      this.steps = data;
      this.reInitiateTable();
    } else {
      let data = this.stackMap["current"].pop();
      this.stackMap["past"].push(data);
      this.steps =
        this.stackMap["current"][this.stackMap["current"].length - 1];
      this.reInitiateTable();
    }
  }

  /**
   * Displays a toaster notification.
   * @param message The message to display.
   * @param isSuccess Whether the notification indicates success.
   * @param showDefaultError Whether to display a default error message.
   */
  popToaster(message: string, isSuccess = true, showDefaultError?: boolean) {
    if (isSuccess) {
      this.toaster.pop({ type: 'success', text: message }, this.toaster.scriptEditorOptions);
    } else {
      this.toaster.pop({ type: 'error', text: showDefaultError ? MessageEnum.SOMETHING_WENT_WRONG : message }, this.toaster.scriptEditorOptions);
    }
  }

  onEditIconClick(row: any) {
    let fields = _.cloneDeep(row.stepParameters);
    let returnVar = row.returnVariable;
    let varInd = fields.findIndex(
      (ele: any) => ele.dataType === "variableSelect"
    );
    let stepDefinition = this.commandDefinitions.find(
      (definition: any) => definition.id === row.commandDefinitionId
    );
    let returnType = stepDefinition?.returnType;
    let isReturnVariableRequired = stepDefinition?.isReturnTypeRequired;
    if (returnVar && varInd < 0) {
      let variables = this.meta.variables?.filter(
        (ele: any) => ele.dataType === returnVar.dataType
      ) ?? [];
      if (!variables.find((v: any) => v.id === returnVar.id)) {
        variables = [returnVar, ...variables];
      }
      fields.push({
        dataType: "variableSelect",
        options: variables.map((ele: any) => ({
          ...ele,
          label: ele.name,
          value: ele.id,
        })),
        isOptional: !isReturnVariableRequired,
        id: "variableSelect",
        isActive: true,
        placeholder: `Select ${returnVar.dataType} Variable`,
        name: "Return Variable",
        labelDescription: variables?.length
          ? ""
          : `No ${returnVar.dataType} Variables Found`,
        value: returnVar.id,
      });
    } else if (
      returnType &&
      !fields.find((ele: any) => ele.dataType === "variableSelect")
    ) {
      let variables = this.meta.variables?.filter(
        (ele: any) => ele.dataType === returnType
      );
      fields.push({
        dataType: "variableSelect",
        options: variables.map((ele: any) => ({
          ...ele,
          label: ele.name,
          value: ele.id,
        })),
        isOptional: !isReturnVariableRequired,
        id: "variableSelect",
        isActive: true,
        placeholder: `Select ${returnType} Variable`,
        name: "Return Variable",
        labelDescription: variables?.length
          ? ""
          : `No ${returnType} Variables Found`,
      });
    }

    let def = this.commandDefinitions.find(
      (definition: any) => row.commandDefinitionId === definition.id
    );
    fields.forEach((ele: any) => {
      let step = def?.parameters?.find((step: any) => step.id === ele.id);
      if (step?.options) {
        ele.options = step.options;
      }
    });

    //change select to 'param_ui_object'
    const matches = row.stepParameters.filter(
      (param: any) => param.id === 'param_ui_object' || param.id === 'param_page'
    );
    if (matches.length > 0) {
      matches.forEach((match: any) => {
        if (match?.uiParams && Object.keys(match.uiParams).length > 0) {
          const index = fields.findIndex((f: any) => f.id === match.id);
          if (index !== -1) {
            fields[index].type = "param_ui_object";
          }
        }
      });
    }
    // if (match?.uiParams && Object.keys(match.uiParams).length > 0) {
    //   const index = fields.findIndex((f:any) => f.id === "param_ui_object");
    //   if(index !== -1){
    //     fields[index].type = "param_ui_object";
    //   }
    // }
    if (row.commandDefinitionId ==="CMD_DEFAULT_ADD_EXPRESSION" ) {
      fields[0].type = "textarea";
      fields[0].dataType = "textarea";
    }

    if (row.commandDefinitionId === 'CMD_DEFAULT_CALL_REST_WEB_SERVICE') {
      const modalRef = this.modalService.open(CmdRestWebServiceModalComponent, {
        centered: true,
        size: 'lg',
        backdrop: 'static',
      });
      modalRef.componentInstance.services = this.meta?.services;
      modalRef.componentInstance.fields = fields;
      modalRef.componentInstance.isEdit = true;
      modalRef.componentInstance.routeParams = this.paramIds;
      modalRef.componentInstance.variables = this.meta.variables;
      modalRef.componentInstance.intellisense = this.intellisenseList;
      modalRef.componentInstance.restApiDraftVariables = this.restApiDraftVariables;
      modalRef.componentInstance.id = row.id;
      modalRef.result.then((data: any) => {
        this.updateDataOnEdit(data, row);
      });
    } else {
      const modalRef = this.modalService.open(EditorItemModalComponent, {
        centered: true,
        windowClass: "add-item-modal",
        backdrop: 'static',
      });
      modalRef.componentInstance.configs = {
        title: this.commandDefinitions.find(
          (ele: any) => ele.id === row.commandDefinitionId
        ).name,
        isEdit: true,
        intellisenseList: row.commandDefinitionId === 'CMD_DEFAULT_ADD_EXPRESSION' ? [] : this.intellisenseList,
      };
      let filterredMeta = _.cloneDeep(this.meta);
      filterredMeta.application = filterredMeta.application.filter((ele: any) => ele.paradigm === stepDefinition.paradigm);
      modalRef.componentInstance.meta = filterredMeta;
      modalRef.componentInstance.fields = fields;
      modalRef.componentInstance.routeParams = this.paramIds;
      modalRef.componentInstance.definitionId = row.commandDefinitionId;
      modalRef.result.then((data) => {
        if (data.fields) {
          this.updateDataOnEdit(data, row);
        }
      });
    }
  }

  updateDataOnEdit(data: any, row: any) {
    let returnVarInd = data.fields.findIndex(
      (ele: any) => ele.dataType === "variableSelect"
    );
    if (returnVarInd >= 0 && data.fields[returnVarInd]?.value) {
      let value = data.fields[returnVarInd].value;
      let stepInd = this.steps.findIndex(
        (ele: any) => ele.row_id === row.row_id
      );
      if (stepInd >= 0) {
        if (!this.steps[stepInd].returnVariable) {
          let varObj = this.meta.variables.find(
            (ele: any) => ele.id === data.fields[returnVarInd].value
          );
          this.steps[stepInd].returnVariable = _.cloneDeep(varObj);
        }
        this.steps[stepInd].returnVariable.id = value;
      }
      data.fields.splice(returnVarInd, 1);
    }
    this.stackMap["past"].push(_.cloneDeep(this.steps));
    row.stepParameters = data.fields.filter(
      (ele: any) => ele.dataType !== "variableSelect"
    );
    this.replaceDataInRow(
      this.steps,
      row.row_id,
      "stepParameters",
      data.fields
    );
    this.stackMap["current"] = [];
    this.stackMap["current"].push(_.cloneDeep(this.steps));
    this.reInitiateTable();
  }

  incrementVersion(version: string): string {
    const match = version.match(/^v(\d+)\.(\d+)\.(\d+)$/);
    if (!match)
      throw new Error(
        "Invalid version format. Expected v<major>.<minor>.<patch>"
      );

    let [_, major, minor, patch] = match;
    const newPatch = parseInt(patch, 10) + 1;

    return `v${major}.${minor}.${newPatch}`;
  }

  onVersionChange(data: any) {
    console.log("script version changed: ", data);
    const match = this.versions?.list?.find((v: any) => v.version === data);
    const id = match ? match.id : null;
    this.selectedScriptLabel = match?.labels ? [match?.labels[0]?.name] : [];
    this.selectedScriptId = id;
    console.log("Matching ID:", id);
    this.versions.active = data;

    this.getScriptById(id);

    // [steps]="steps"
    // [meta]="scriptMeta"
    // [versions]="versions"
    // [displayVersionList]="displayVersionList"
    // [selectedAgent]="selectedAgent"
    // (actions)="triggerActions($event)"
    // [isDataLoading]="isDataLoading"
    // [intellisenseList]="intellisenseList">
  }

  openSaveScriptModal() {
    const modalRef = this.modalService.open(SaveScriptModalComponent, {
      centered: true,
      windowClass: "add-item-modal",
      backdrop: 'static',
    });

    modalRef.componentInstance.configs = {
      version: this.incrementVersion(
        this.formattedPayload?.version
          ? this.formattedPayload?.version
          : "v1.0.1"
      ),
      labels: this.formattedPayload.labels,
    };
    modalRef.result.then((data: any) => {
      this.updateScript(data.labels);
    });
  }

  getScriptById(id: string) {
    this.isDataLoading = true;
    this.loaderText = "Loading script...";
    let scriptID: string;

    if (id === "") {
      scriptID =
        this.selectedScriptId === ""
          ? this.paramIds.scriptId
          : this.selectedScriptId;
    } else {
      scriptID = id;
    }

    this.restService
      .getScriptById(
        this.paramIds.projectId,
        this.paramIds.automationId,
        scriptID
      )
      .subscribe(
        (res: any) => {
          this.isDataLoading = false;
          if (res?.data) {
            this.meta.script = res?.data;
            if (this.meta?.script?.tcgTestCaseResultName) {
              this.isMapped = true;
            } else {
              this.isMapped = false;
            }
            let generatedSteps = _.cloneDeep(this.steps);
            this.generateStepsPayload(generatedSteps);

            let responseData = res.data;
            this.formattedPayload = {
              id: responseData.id,
              scriptId: responseData.scriptId,
              name: responseData.name,
              increment: responseData.increment,
              version: responseData.version,
              labels: responseData?.labels
                ? responseData?.labels
                : this.selectedScriptLabel,
              type: responseData.type,
              variables: responseData.variables,
              scriptParameters: responseData.scriptParameters,
              steps: generatedSteps,
              entityUsageList: [],
            };

            if (id === "") {
              this.openSaveScriptModal();
            } else {
              this.isDataLoading = false;

              // set steps
              this.steps =
                responseData?.steps?.sort(
                  (a: any, b: any) => a?.sequence - b?.sequence
                ) || [];

              this.proceedInitialProcess();

              // set variables
              responseData.variables?.forEach((item: any) => {
                if (!item.active) {
                  return;
                }
                this.meta.variables.children.push({
                  ...item,
                  level: 1,
                  displayName: item.name,
                  key: "variable",
                  menu: this.contextMenu,
                  // assetIcon: "assets/images/editor-tree/tree-node.png",
                  style: { cursor: "pointer" },
                });
              });
            }
          }
        },
        () => {
          this.isDataLoading = false;
        }
      );
  }

  updateScript(labels?: any) {
    let scriptID =
      this.selectedScriptId === ""
        ? this.paramIds.scriptId
        : this.selectedScriptId;

    const version = this.incrementVersion(
      this.formattedPayload?.version ? this.formattedPayload?.version : "v1.0.1"
    );
    this.formattedPayload.labels = [labels];
    this.formattedPayload.version = version;

    this.isSaving = true;
    this.isDataLoading = true;
    this.loaderText = "Saving script...";

    this.restService
      .updateScript(
        this.paramIds.projectId,
        this.paramIds.automationId,
        scriptID,
        this.formattedPayload
      )
      .subscribe(
        (updateRes: any) => {
          console.log(updateRes);
          if (updateRes.status === 200) {
            this.isSaving = false;
            this.isDataLoading = false;
            if (version) {
              this.versions.list?.push({
                id: updateRes?.data?.id,
                scriptId: updateRes?.data?.scriptId,
                version,
                labels: [
                  {
                    name: labels,
                  },
                ],
              });
              this.versions.active = version;
            }
            this.steps = updateRes.data?.steps;
            this.reInitiateTable();
            this.popToaster('Script Update - Success');
          }
        },
        (error: any) => {
          this.isSaving = false;
          this.isDataLoading = false;
          if (error?.errors?.length) {
            error.errors.forEach((err: any) => {
              this.popToaster(
                `Mandatory parameter '${err.parameterName}' in step ${err.sequence} of script ${err.scriptName} has not been assigned a value.`,
                false
              );
            });
          } else {
            this.popToaster("", false, true);
          }
        }
      );
  }

  saveScript() {
    const missingParameters = this.findUnsetUsedParameters(this.steps);
    if (!missingParameters.length) {
      this.persistScript();
      return;
    }

    const quoted = missingParameters.map((name) => `"${name}"`).join(", ");
    const label = missingParameters.length === 1 ? "Parameter" : "Parameters";
    const verb = missingParameters.length === 1 ? "requires" : "require";
    const modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass: "delete-confirmation-window",
      centered: true,
      backdrop: "static",
    });
    modalRef.componentInstance.data = {
      title: "Parameter value missing",
      description: `${label} ${quoted} ${verb} a value. Save the script anyway?`,
      confirmText: "Proceed",
      cancelText: "Cancel",
    };
    modalRef.result.then(
      (confirmed) => {
        if (confirmed) {
          this.persistScript();
        }
      },
      () => {}
    );
  }

  private persistScript() {
    const version = this.versions.active;
    this.isSaving = true;
    this.isDataLoading = true;
    let formattedPayload: any = {};
    this.loaderText = "Saving script...";
    let generatedSteps = _.cloneDeep(this.steps);
    this.generateStepsPayload(generatedSteps);
    this.restService
      .getScriptById(
        this.paramIds.projectId,
        this.paramIds.automationId,
        this.paramIds.scriptId
      )
      .subscribe(
        (res: any) => {
          if (res?.data) {
            // find out used entities
            let entities: any = [];
            generatedSteps.forEach((step: any) => {
              step?.stepParameters.forEach((parameter: any) => {
                switch (parameter.dataType?.toLowerCase()) {
                  case "application":
                    entities.push({
                      type: "APPLICATION",
                      entityId: parameter.value,
                      stepId: step.id,
                      sequence: step.sequence
                    });
                    break;
                  case "script":
                    entities.push({
                      type: "REUSABLE_SCRIPT",
                      entityId: parameter.value,
                      stepId: step.id,
                      sequence: step.sequence
                    });
                    break;
                  case "page":
                    entities.push({
                      type: "PAGE",
                      entityId: parameter.value,
                      stepId: step.id,
                      sequence: step.sequence
                    });
                    break;
                  case "uiobject":
                    entities.push({
                      type: "UI_OBJECT",
                      entityId: parameter.value,
                      stepId: step.id,
                      sequence: step.sequence
                    });
                    break;
                  case "service":
                    entities.push({
                      type: "SERVICE",
                      entityId: parameter.value,
                      stepId: step.id,
                      sequence: step.sequence
                    });
                    break;
                  case "operation":
                    entities.push({
                      type: "OPERATION",
                      entityId: parameter.value,
                      stepId: step.id,
                      sequence: step.sequence
                    });
                    break;
                  case "globalvariable":
                    entities.push({
                      type: "GLOBAL_VARIABLE",
                      entityId: parameter.value,
                      stepId: step.id,
                      sequence: step.sequence
                    });
                    break;
                  case "uploadfiles":
                    // value is an array of file connection IDs — push one entry per file
                    if (Array.isArray(parameter.value)) {
                      parameter.value.forEach((fileId: string) => {
                        entities.push({
                          type: "FILE",
                          entityId: fileId,
                          stepId: step.id,
                          sequence: step.sequence
                        });
                      });
                    }
                    break;
                }
              });
            });

            let responseData = res.data;
            formattedPayload = {
              id: responseData.id,
              scriptId: responseData.scriptId,
              name: responseData.name,
              increment: responseData.increment,
              version: version || responseData.version,
              labels: responseData?.labels ? responseData?.labels : [],
              type: responseData.type,
              variables: responseData.variables,
              scriptParameters: responseData.scriptParameters,
              steps: generatedSteps,
              entityUsageList: entities,
            };
            this.restService
              .updateScript(
                this.paramIds.projectId,
                this.paramIds.automationId,
                this.paramIds.scriptId,
                formattedPayload
              )
              .subscribe(
                (updateRes: any) => {
                  console.log(updateRes);
                  if (updateRes.status === 200) {
                    this.isSaving = false;
                    this.isDataLoading = false;
                    if (version) {
                      this.versions.list?.push({
                        scriptId: updateRes.id,
                        version,
                      });
                      this.versions.active = version;
                    }
                    this.steps = updateRes.data?.steps ?? this.steps;
                    this.actions.emit({ type: 'reloadServices' });
                    this.reInitiateTable();
                    this.popToaster('Script Update - Success');
                  }
                },
                (error: any) => {
                  this.isSaving = false;
                  this.isDataLoading = false;
                  if (error?.errors?.length) {
                    error.errors.forEach((err: any) => {
                      this.popToaster(
                        `Mandatory parameter '${err.parameterName}' in step ${err.sequence} of script ${err.scriptName} has not been assigned a value.`,
                        false
                      );
                    });
                  } else {
                    this.popToaster("", false, true);
                  }
                }
              );
          }
        },
        () => {
          this.isSaving = false;
          this.isDataLoading = false;
        }
      );
  }

  /**
   * Input parameters referenced by a command, and object parameters on a command,
   * must have a value before the script is saved.
   */
  private findUnsetUsedParameters(steps: any[]): string[] {
    const missing = new Set<string>();
    const inputParameters = (this.meta?.parameters || []).filter(
      (param: any) => String(param?.type || "INPUT").toUpperCase() !== "OUTPUT"
    );

    const visit = (list: any[]) => {
      list?.forEach((step: any) => {
        const referenced = this.extractParameterTokens(this.collectStepText(step));
        referenced.forEach((token) => {
          const param = inputParameters.find((item: any) => this.sameParameterName(item, token));
          if (param && !String(param.defaultValue ?? "").trim()) {
            missing.add(param.name || param.label || token);
          }
        });

        step?.stepParameters?.forEach((parameter: any) => {
          const uiParams = parameter?.uiParams;
          if (!uiParams || typeof uiParams !== "object") {
            return;
          }
          Object.entries(uiParams).forEach(([key, value]) => {
            if (value == null || !String(value).trim()) {
              missing.add(key);
            }
          });
        });

        if (step?.children?.length) {
          visit(step.children);
        } else if (step?.steps?.length) {
          visit(step.steps);
        }
      });
    };

    visit(steps);
    return [...missing];
  }

  private collectStepText(step: any): string {
    const parts: string[] = [];
    const push = (value: unknown) => {
      if (typeof value === "string") {
        parts.push(value);
      } else if (Array.isArray(value)) {
        value.forEach((item) => push(item));
      }
    };
    step?.stepParameters?.forEach((parameter: any) => {
      push(parameter?.value);
      push(parameter?.displayValue);
      if (parameter?.uiParams && typeof parameter.uiParams === "object") {
        Object.values(parameter.uiParams).forEach((value) => push(value));
      }
    });
    return parts.join(" ");
  }

  private extractParameterTokens(text: string): string[] {
    const tokens: string[] = [];
    const pattern = /\$\{([^}]+)\}|\$([A-Za-z_][\w]*)/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text))) {
      tokens.push(match[1] || match[2]);
    }
    return tokens;
  }

  private sameParameterName(param: any, token: string): boolean {
    const names = [param?.name, param?.label, param?.displayName]
      .filter((name) => typeof name === "string")
      .map((name: string) => name.replace(/^\$/, ""));
    return names.includes(token.replace(/^\$/, ""));
  }

  generateStepsPayload(arr: any[] = this.steps) {
    arr?.forEach((step: any, ind: number) => {
      let stepParameters = step.stepParameters;
      let children = step.children;
      arr[ind] = {
        commandDefinitionId: step.commandDefinitionId,
        name: step.name === undefined ? "" : step.name,
        sequence: ind + 1,
        stepParameters: [],
        id: step.id || undefined,
        returnVariableId: step.returnVariable?.id || undefined,
      };
      stepParameters?.forEach((param: any) => {
        arr[ind].stepParameters.push({
          customInput: param.customInput,
          dataType: param.dataType,
          description: param.description,
          id: param.id,
          isActive: param.isActive,
          isOptional: param.isOptional,
          name: param.name,
          order: param.order,
          uiParams: param.uiParams || undefined,
          value: param.value,
          displayValue:
            param.displayValue || this.resolveParamDisplayValue(param) || undefined,
        });
      });
      if (children?.length) {
        arr[ind].steps = children;
        this.generateStepsPayload(arr[ind].steps);
      }
    });
  }

  loadActivities() {
    const modalRef = this.modalService.open(
      ValidateRecordedActivitiesComponent,
      {
        centered: true,
        windowClass: "scrip-mapping-modal",
        backdrop: 'static',
      }
    );
    modalRef.componentInstance.type = 'web';
    modalRef.componentInstance.routeParams = this.paramIds;
    modalRef.componentInstance.meta = this.meta;
    modalRef.result.then((data: any) => {
      if (data?.length) {
        if(!this.steps) this.steps = [];
        this.stackMap["past"].push(_.cloneDeep(this.steps));
        this.steps = this.steps.concat(data);
        this.stackMap["current"].push(_.cloneDeep(this.steps));
        this.meta.page = {};
        this.meta.uiObject = {};
        this.actions.emit({ type: "reloadApplications" });
        // saveRecordedScript can create script/global variables
        this.actions.emit({ type: "reloadVariables" });
        this.reInitiateTable();
      }
    });
  }

  openWebRecorder() {
    this.openUserInfoModal();
  }

  openAIRecorder() {
    this.openInstructions();
  }

  openUserInfoModal() {
    const modalRef = this.modalService.open(
      EditorGenerateConfirmationComponent,
      {
        centered: true,
        windowClass: "add-item-record-modal",
        backdrop: 'static',
      }
    );
    modalRef.componentInstance.projectId = this.paramIds.projectId;
    modalRef.result.then((result) => {
      this.loadActivities();
    });
  }

  openInstructions() {
    const modalRef = this.modalService.open(StepsConfirmationModalComponent, {
      centered: true,
      windowClass: "add-item-record-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = {
      title: "Steps to Generate Test Scripts",
      subTitle: "Steps to generate the Test Scripts automatically",
      steps: ['Click the “Start Now” button to initiate the generation process.', 'Select the appropriate Application and enter the corresponding Base URL for which you wish to generate test scripts.', 'Provide the necessary role-based credentials to ensure accurate activity tracking.', 'Once all steps are completed, click “Generate Scripts” to populate and review the generated test scripts.'],
      buttonText: "Start Now"
    };

    modalRef.result.then((result) => {
      this.loadAndValidateActivities();
    });
  }

  loadAndValidateActivities() {
    const fields = [
      {
        name: "Mapped Test Case",
        type: "text-display",
        key: "testCase",
      },
      {
        name: "Application Name",
        type: "dropdown",
        key: "appName",
        options: this.meta?.application,
      },
      {
        name: "Base URL",
        type: "text-input",
        key: "baseUrl",
      },
      {
        name: "Role-Based Credentials",
        type: "button",
        key: "add",
        label: "Add New Credential",
        icon: "assets/images/virtusa-icons/add-icon.svg",
      },
    ];

    const modalRef = this.modalService.open(
      ValidateRecordedActivitiesComponent,
      {
        centered: true,
        size: "xl",
        windowClass: "add-item-modal",
        backdrop: 'static',
      }
    );
    modalRef.componentInstance.type = "ai";
    modalRef.componentInstance.configs = {
      title: "Load & Validate Recorded Activities",
      tableData: {
        columns: credentialTableColumns,
        rows: [],
      },
    };
    modalRef.componentInstance.fields = fields;
    modalRef.componentInstance.model = {
      testCase: this.meta?.script?.tcgTestCaseResultName || '',
      testCaseId: this.meta.script.tcgTestCaseResultId || ''
    };
    modalRef.componentInstance.routeParams = this.paramIds;

    modalRef.result.then((result) => {
      if (result) {
        if(!this.steps) this.steps = [];
        this.stackMap["past"].push(_.cloneDeep(this.steps));
        result?.forEach((ele: any) => {
          if (!ele.id || !this.steps?.find((step: any) => step.id === ele.id)) {
            this.steps.push(ele);
          }
        });
        this.actions.emit({ type: 'reloadServices' });
        // saveRecordedScript can create script/global variables
        this.actions.emit({ type: "reloadVariables" });
        this.stackMap["current"].push(_.cloneDeep(this.steps));
        this.reInitiateTable();
      }
    });
  }

  openApiAutomation() {
    const modalRef = this.modalService.open(
      ValidateRecordedActivitiesComponent,
      {
        centered: true,
        size: "lg",
        windowClass: "add-item-modal",
        backdrop: 'static',
      }
    );
    modalRef.componentInstance.type = "api";
    modalRef.componentInstance.configs = {
      title: "Generate API test script",
    };
    modalRef.componentInstance.model = {
      testCase: this.meta?.script?.tcgTestCaseResultName || '',
      testCaseId: this.meta.script.tcgTestCaseResultId || '',
      testSuiteName: this.meta?.script?.automationScriptGroup?.name || '',
      scriptName: this.meta?.script?.name || '',
    };
    modalRef.componentInstance.routeParams = this.paramIds;

    modalRef.result.then((result) => {
      if (result) {
        if(!this.steps) this.steps = [];
        this.stackMap["past"].push(_.cloneDeep(this.steps));
        result?.forEach((ele: any) => {
          if (!ele.id || !this.steps?.find((step: any) => step.id === ele.id)) {
            this.steps.push(ele);
          }
        });
        this.actions.emit({ type: 'reloadServices' });
        this.stackMap["current"].push(_.cloneDeep(this.steps));
        this.reInitiateTable();
      }
    });
  }

  openMobileAutomation() {
    const modalRef = this.modalService.open(
      ValidateRecordedActivitiesComponent,
      {
        centered: true,
        size: "xl",
        windowClass: "add-item-modal",
        backdrop: 'static',
      }
    );
    modalRef.componentInstance.type = "mobile";
    modalRef.componentInstance.configs = {
      title: "Load & Validate Recorded Activities",
      tableData: {
        columns: credentialTableColumns,
        rows: [],
      }
    };
    modalRef.componentInstance.model = {
      testCase: this.meta?.script?.tcgTestCaseResultName || '',
      testCaseId: this.meta.script.tcgTestCaseResultId || ''
    };
    modalRef.componentInstance.routeParams = this.paramIds;
    modalRef.componentInstance.meta = this.meta;

    modalRef.result.then((data) => {
      if (data?.length) {
        if(!this.steps) this.steps = [];
        this.stackMap["past"].push(_.cloneDeep(this.steps));
        this.steps = this.steps.concat(data);
        this.stackMap["current"].push(_.cloneDeep(this.steps));
        this.meta.page = {};
        this.meta.uiObject = {};
        this.actions.emit({ type: "reloadApplications" });
        // saveRecordedScript can create script/global variables
        this.actions.emit({ type: "reloadVariables" });
        this.reInitiateTable();
      }
    });
  }

  getTooltipAi() {
    return !this.isAiRecorderEnabled ? 'Ai Recorder is not enabled in assets configuration' : 'Please map a test case to use AI Recorder';
  }

  isTooltipDisabled(type: string) {
    if (type === 'api') {
      return this.isMapped && this.isApiAutomationEnabled;
    } else {
      return this.isMapped && this.isAiRecorderEnabled;
    }
  }

  openSettings(): void {
    // Convert versions list to format required by select dropdown
    const versionOptions = this.versions?.list?.map((v: any) => ({
      label: v.version,
      value: v.version
    })) || [];

    let editorSettingsFields = [
      {
        key: "version",
        name: "Script version",
        type: "select",
        placeholder: "Select Script version",
        options: versionOptions,
        value: this.versions?.active,
        validations: {
          required: true,
        },
        customClass: "mt-3",
      },
      {
        key: "agent",
        name: "Device/Execution Engine",
        type: "menu-dropdown",
        placeholder: "Select device/execution engine",
        options: this.globalEngines,
        value: this.selectedAgent,
        validations: {
          required: true,
        },
        customClass: "mt-3",
      },
    ];
    const modalRef = this.modalService.open(EditorSettingsModalComponent, {
      centered: true,
      size:"lg",
      windowClass: "scrip-mapping-modal",
      backdrop: 'static',
    });
    
    modalRef.componentInstance.configs = {
      title: "Editor Settings",
      saveBtnText: "Save",
      data: {
        scriptName: this.meta?.script?.name || '',
        testSuite: this.meta?.script?.automationScriptGroup?.name || '',
        // scriptVersion: this.versions?.active || '',
        mappedTestCase: this.meta?.script?.tcgTestCaseResultName || '',
      },
      fields: editorSettingsFields // Add fields from editorSettingsFields if needed
    };
    
    modalRef.result.then((result: any) => {
      if (result) {
        console.log('Editor settings updated:', result);
        this.selectedAgent = result?.value?.agent;
        
        // Handle settings update here
      }
    }).catch(() => {
      // User dismissed modal
    });
  }
}
